import React, { useEffect, useState } from "react";
import {
  getClientes,
  postRelacionClienteRubro,
} from "../../componentes/http/actions/ClientesAction";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Typography,
  Box,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import {Settings as SettingsIcon} from "@mui/icons-material";
import FilterComponent from "../../componentes/data/FilterComponent";
import AgregarRelacionClienteRubroModal from "../../componentes/modal/Clientes/AgregarRelacionClienteRubroModal";

const GestionRubros = () => {
  const [clientes, setClientes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [openRelacionModal, setOpenRelacionModal] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterHabilitado, setFilterHabilitado] = useState("todos");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = () => {
    setIsLoading(true);
    getClientes(localStorage.getItem("token"))
      .then((response) => {
        setClientes(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        console.log(error);
        setClientes([]);
        setSnackbar({
          open: true,
          message: "Error al cargar clientes",
          severity: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
        setInitialDataLoaded(true);
      });
  };

  const filteredClientes = clientes
    .filter((cliente) => {
      const matchesSearch = cliente.nombre
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesFilter =
        filterHabilitado === "todos" ||
        (filterHabilitado === "habilitado" && cliente.habilitado === true) ||
        (filterHabilitado === "deshabilitado" && cliente.habilitado === false);
      return matchesSearch && matchesFilter;
    })
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPage(0);
  };
  
  const handleFilterChange = (newFilter) => {
    setFilterHabilitado(newFilter);
    setPage(0);
  };

  const filterOptions = [
    { value: "todos", label: "Todos" },
    { value: "habilitado", label: "Habilitados" },
    { value: "deshabilitado", label: "Deshabilitados" },
  ];

  const handleRelacionarRubros = async (relacion) => {
    try {
      setIsLoading(true);
      await postRelacionClienteRubro(localStorage.getItem("token"), relacion);
      setSnackbar({
        open: true,
        message: "Rubros relacionados con éxito",
        severity: "success",
      });
      fetchClientes(); 
      setOpenRelacionModal(false);
    } catch (error) {
      console.error("Error al relacionar rubros:", error);
      setSnackbar({
        open: true,
        message: "Error al relacionar rubros",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container direction="column" spacing={2} sx={{paddingTop: {xs: '20px', sm: '10px'}}}>
      <Grid item>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Gestión de Rubros</Typography>
        </Grid>
        <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>
          Administre la relación entre clientes y rubros
        </Typography>
      </Grid>
      
      <Grid item>
        <FilterComponent
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          filterValue={filterHabilitado}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions}
        />
      </Grid>
      
      <Grid item>
        <TableContainer component={Paper}>
          {isLoading && !initialDataLoaded ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : filteredClientes.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Id</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Rubros Asociados</TableCell>
                  <TableCell sx={{ display: "flex", justifyContent: "center" }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClientes.map((cliente, index) => (
                  <TableRow key={index}>
                    <TableCell>{cliente.id}</TableCell>
                    <TableCell>{cliente.nombre}</TableCell>
                    <TableCell>
                      {cliente.nombreRubro && cliente.nombreRubro.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {cliente.nombreRubro.map((rubro, idx) => (
                            <Chip
                              key={idx}
                              label={rubro}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Sin rubros asociados
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Tooltip title="Gestionar Rubros">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setClienteSeleccionado(cliente);
                              setOpenRelacionModal(true);
                            }}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box p={3}>
              <Typography>
                No se encontraron clientes con los criterios de búsqueda actuales.
              </Typography>
            </Box>
          )}
        </TableContainer>
        
        <TablePagination
          component="div"
          count={clientes.filter((cliente) => {
            const matchesSearch = cliente.nombre
              .toLowerCase()
              .includes(searchTerm.toLowerCase());
            const matchesFilter =
              filterHabilitado === "todos" ||
              (filterHabilitado === "habilitado" && cliente.habilitado === true) ||
              (filterHabilitado === "deshabilitado" && cliente.habilitado === false);
            return matchesSearch && matchesFilter;
          }).length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Grid>

      <AgregarRelacionClienteRubroModal
        open={openRelacionModal}
        onClose={() => setOpenRelacionModal(false)}
        onSubmit={handleRelacionarRubros}
        cliente={clienteSeleccionado}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default GestionRubros;